import type { TextParagraph, TextRun } from '../model';

// #756 -- shared layout for standalone rich-text annotations, used by both the live paper
// (PaperVectorLayer) and the SVG string export (svgExport) so the two can't drift. Pure: it only
// positions runs; escaping is the caller's job (React escapes on the paper, svgExport escapes the
// string).

// Font sizes are authored in points. Paper user units are CSS px at 100% zoom, and CSS fixes
// 96 px per inch, so 1 pt = 96/72 px.
export const PX_PER_POINT = 96 / 72;
export const DEFAULT_TEXT_FONT_SIZE_PT = 12;
export const DEFAULT_TEXT_FONT_FAMILY = 'Arial, sans-serif';

const LINE_HEIGHT = 1.25;
const ASCENT = 0.8;
const SCRIPT_SCALE = 0.7;
const SUBSCRIPT_SHIFT = 0.25;
const SUPERSCRIPT_SHIFT = -0.4;

export interface TextFont {
  readonly fontFamily: string;
  readonly fontSizePx: number;
  readonly bold: boolean;
  readonly italic: boolean;
}

// Returns the advance width of `text` in px. The browser passes a canvas-backed measure; tests and
// the headless export use the estimate.
export type TextMeasure = (text: string, font: TextFont) => number;

export const estimateTextWidth: TextMeasure = (text, font) =>
  Array.from(text).length * font.fontSizePx * (font.bold ? 0.6 : 0.55);

export interface LaidOutSpan extends TextFont {
  readonly text: string;
  // Absolute baseline for this span, sub/superscript shift included.
  readonly y: number;
  // The same shift relative to the previous span (the first span: to the line baseline). Renderers
  // emit this as `dy`, not `y`: an absolute `y` on a <tspan> starts a new text chunk, and
  // text-anchor then centres/right-aligns each chunk separately, tearing the line apart.
  // (`baseline-shift` would avoid both, but Firefox ignores it.)
  readonly dy: number;
}

export interface LaidOutLine {
  readonly x: number;
  readonly baseline: number;
  readonly anchor: 'start' | 'middle' | 'end';
  readonly spans: readonly LaidOutSpan[];
}

export interface TextLayout {
  readonly lines: readonly LaidOutLine[];
  readonly width: number;
  readonly height: number;
}

function runFont(run: TextRun, scale: number): { readonly font: TextFont; readonly baseSizePx: number } {
  const baseSizePx = (run.fontSize ?? DEFAULT_TEXT_FONT_SIZE_PT) * PX_PER_POINT * scale;
  return {
    baseSizePx,
    font: {
      fontFamily: run.fontFamily ?? DEFAULT_TEXT_FONT_FAMILY,
      fontSizePx: run.script ? baseSizePx * SCRIPT_SCALE : baseSizePx,
      bold: run.bold ?? false,
      italic: run.italic ?? false
    }
  };
}

// `origin` is the block's top-left corner in px; `scale` multiplies every authored size (viewport
// zoom on the paper, 1 for export).
export function layoutText(
  paragraphs: readonly TextParagraph[],
  origin: { readonly x: number; readonly y: number },
  measure: TextMeasure = estimateTextWidth,
  scale = 1
): TextLayout {
  const defaultSizePx = DEFAULT_TEXT_FONT_SIZE_PT * PX_PER_POINT * scale;
  const measured = paragraphs.map((paragraph) => {
    const runs = paragraph.runs.map((run) => {
      const { font, baseSizePx } = runFont(run, scale);
      return { run, font, baseSizePx, width: measure(run.text, font) };
    });
    const lineSizePx = runs.length > 0 ? Math.max(...runs.map((entry) => entry.baseSizePx)) : defaultSizePx;
    return {
      align: paragraph.align ?? 'left',
      runs,
      lineSizePx,
      width: runs.reduce((sum, entry) => sum + entry.width, 0)
    };
  });
  const width = measured.length > 0 ? Math.max(...measured.map((line) => line.width)) : 0;

  let top = origin.y;
  const lines = measured.map((line): LaidOutLine => {
    const baseline = top + line.lineSizePx * ASCENT;
    top += line.lineSizePx * LINE_HEIGHT;
    let previousY = baseline;
    const spans = line.runs.map(({ run, font, baseSizePx }) => {
      const y =
        baseline +
        (run.script === 'sub'
          ? baseSizePx * SUBSCRIPT_SHIFT
          : run.script === 'super'
            ? baseSizePx * SUPERSCRIPT_SHIFT
            : 0);
      const dy = y - previousY;
      previousY = y;
      return { ...font, text: run.text, y, dy };
    });
    if (line.align === 'center') return { x: origin.x + width / 2, baseline, anchor: 'middle', spans };
    if (line.align === 'right') return { x: origin.x + width, baseline, anchor: 'end', spans };
    return { x: origin.x, baseline, anchor: 'start', spans };
  });

  return { lines, width, height: top - origin.y };
}

// The plain text of a block, one line per paragraph -- for accessible names and search.
export function plainText(paragraphs: readonly TextParagraph[]): string {
  return paragraphs.map((paragraph) => paragraph.runs.map((run) => run.text).join('')).join('\n');
}
